/**
 * #1005 follow-up: 公開の「注文完結」ページ。
 *
 * 厨房伝票の QR (URL 型: /complete?o=注文ID&t=署名トークン) をスタッフがスマホの
 * 標準カメラで読むと、このページが開き、token だけで提供/配送完了にする
 * (ログイン不要)。token は HMAC 署名済でサーバー側で検証するため、所持ベースの
 * 保護は効いている。アプリ内スキャナ (StoreKitchen) と併存。
 */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'

type State =
  | { kind: 'loading' }
  | { kind: 'done'; orderType: string; storeName?: string }
  | { kind: 'error'; message: string }

export default function OrderComplete() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const orderId = params.get('o') || ''
  const token = params.get('t') || ''
  const hasParams = !!orderId && !!token
  const [state, setState] = useState<State>({ kind: 'loading' })
  // StrictMode の二重実行 / 二重完結を防ぐ。
  const ranRef = useRef(false)

  useEffect(() => {
    // 不正パラメータは render 側で処理する（effect 内で同期 setState を呼ばない）。
    if (!hasParams || ranRef.current) return
    ranRef.current = true
    api
      .completeByToken(orderId, token)
      .then((r) => setState({ kind: 'done', orderType: r.orderType, storeName: r.storeName }))
      .catch(() => setState({ kind: 'error', message: t('storeKitchen.completeError') }))
  }, [hasParams, orderId, token, t])

  // パラメータ不足は即エラー表示（API を叩かない）。
  if (!hasParams) {
    return (
      <div className="mobile-container safe-top safe-bottom min-h-screen bg-bg-primary flex flex-col items-center justify-center px-6 text-center">
        <XCircle className="w-16 h-16 text-yokoso-red" />
        <div className="mt-4 text-lg font-semibold text-text-primary">
          {t('storeKitchen.completeInvalid')}
        </div>
      </div>
    )
  }

  return (
    <div className="mobile-container safe-top safe-bottom min-h-screen bg-bg-primary flex flex-col items-center justify-center px-6 text-center">
      {state.kind === 'loading' && (
        <>
          <Loader2 className="w-12 h-12 text-navy animate-spin" />
          <div className="mt-4 text-text-secondary">{t('storeKitchen.completeLoading')}</div>
        </>
      )}
      {state.kind === 'done' && (
        <>
          <CheckCircle2 className="w-20 h-20 text-matcha" />
          <div className="mt-4 text-2xl font-bold text-text-primary">
            {state.orderType === 'delivery'
              ? t('storeKitchen.completeDelivered')
              : t('storeKitchen.completeServed')}
          </div>
          {state.storeName && (
            <div className="mt-1 text-sm text-text-secondary">{state.storeName}</div>
          )}
        </>
      )}
      {state.kind === 'error' && (
        <>
          <XCircle className="w-16 h-16 text-yokoso-red" />
          <div className="mt-4 text-lg font-semibold text-text-primary">{state.message}</div>
        </>
      )}
    </div>
  )
}
